import { createAsyncThunk } from '@reduxjs/toolkit';

import ApiClient from 'api/axiosClient';
import salesApiClient from 'api/salesApiClient';
import { buildQueryString } from 'utils';
import { notifyError, notifySuccess } from 'utils/notifyConfig';

export const getMyAppointments = createAsyncThunk(
  'sales/myAppointments/get',
  async (params, { rejectWithValue }) => {
    const utcOffset = -new Date().getTimezoneOffset();
    const today = new Date().toLocaleDateString('en-CA');
    const query = buildQueryString({ ...params, utcOffset, today });
    try {
      return await ApiClient.get(`/sales/my-appointments?${query}`);
    } catch (error) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        notifyError(error.message);
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getMyAppointmentsNotes = createAsyncThunk(
  'sales/notes/get',
  async (uuid, { rejectWithValue }) => {
    try {
      const data = await ApiClient.get(`/sales/customer-appointments/${uuid}/notes`);
      return data;
    } catch (error) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        notifyError(error.message);
        return rejectWithValue(error.message);
      }
    }
  }
);

export const addNotes = createAsyncThunk(
  'sales/addNotes/post',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(
        `/sales/customer-appointments/${data.uuid}/notes`,
        data.note
      );
      notifySuccess('Appointment successfully created');
      return response;
    } catch (error) {
      notifyError(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const refuseAppointment = createAsyncThunk(
  'sales/refuse/post',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`/sales/customer-appointments/outcomes`, data, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Appointment status updated successfully');
      return response;
    } catch (error) {
      notifyError(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const getChildAppointments = createAsyncThunk(
  'sales/childAppointments/get',
  async (uuid, { rejectWithValue }) => {
    try {
      return await ApiClient.get(`/sales/customer-appointments/${uuid}/children`);
    } catch (error) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        notifyError(error.message);
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getCustomerAppointmentsByLeadId = createAsyncThunk(
  'sales/customerAppointmentsByLeadId/get',
  async (leadId, { rejectWithValue }) => {
    try {
      return await ApiClient.get(`/sales/customer-appointments/sales/${leadId}`);
    } catch (error) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getLeadPassportDataStatus = createAsyncThunk(
  'sales/leads/passportDataStatus/get',
  async (leadId, { rejectWithValue }) => {
    try {
      return await salesApiClient.get(`leads/${leadId}/passport-data-status`);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateLeadPassportData = createAsyncThunk(
  'sales/leads/passportData/update',
  async ({ leadId, data }, { rejectWithValue }) => {
    try {
      return await salesApiClient.put(`leads/${leadId}`, data, {});
    } catch (error) {
      notifyError(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const getLinkedProcessList = createAsyncThunk(
  'sales/linkedProcessList/get',
  async (customerId, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get(`erp/customers/${customerId}/auto-configure-ont`);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const restartPonAddress = createAsyncThunk(
  'sales/restartPonAddress/post',
  async (leadId, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(`leads/${leadId}/restart-pon-address`);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkErpCredentials = createAsyncThunk(
  'myAppointments/erp-credentials/check',
  async (email, { rejectWithValue }) => {
    try {
      return await salesApiClient.get(
        `users/erp-credentials/check?email=${encodeURIComponent(email)}`
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveErpCredentials = createAsyncThunk(
  'myAppointments/erp-credentials/save',
  async ({ uuid, ...data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`users/${uuid}/erp-credentials`, data);
      notifySuccess('ERP credentials saved successfully');
      return response;
    } catch (error) {
      notifyError(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const updatePonErp = createAsyncThunk(
  'myAppointments/updatePonErp',
  async (
    { leadId, pon, deviceType, configurationType, wifiName, wifiPassword },
    { rejectWithValue }
  ) => {
    try {
      const response = await salesApiClient.post(`leads/${leadId}/update-pon-erp`, {
        pon,
        deviceType,
        configurationType,
        wifiName,
        wifiPassword,
      });
      notifySuccess('PON updated successfully');
      return response;
    } catch (error) {
      notifyError(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const getProcessSignalLevel = createAsyncThunk(
  'myAppointments/processSignalLevel/get',
  async (processId, { rejectWithValue }) => {
    try {
      return await salesApiClient.get(`erp/process/${processId}/signal-level`);
    } catch (error) {
      return rejectWithValue(null);
    }
  }
);

export const updateStationParameters = createAsyncThunk(
  'myAppointments/stationParameters/update',
  async ({ leadUuid, processId, body }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.post(
        `erp/customers/${leadUuid}/processes/${processId}/station-parameters`,
        body
      );
      notifySuccess('Parameters updated successfully');
      return response;
    } catch (error) {
      notifyError(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const ReScheduleAppointment = createAsyncThunk(
  'sales/reschedule/post',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(
        `/sales/customer-appointments/${data.uuid}/reschedule`,
        data
      );
      return response;
    } catch (error) {
      notifyError(error.message);
      return rejectWithValue(error.message);
    }
  }
);
